'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { PostLoginActionType, postLoginActionsStorage } from './postLoginActions';
import { ChangeEmailModalDynamic } from './ChangeEmailModal.dynamic';
import { ChangeBillingAddressModalDynamic } from './ChangeBillingAddressModal.dynamic';
import { TACReconfirmationModalDynamic } from './TACReconfirmationModal.dynamic';

/**
 * PostLoginActionsHandler monitors localStorage for pending post-login actions
 * and displays them sequentially (one at a time) to the user.
 *
 * Actions are removed from the queue when:
 * - User successfully completes the action
 * - User dismisses/closes the modal
 * - User logs out
 */
export const PostLoginActionsHandler = () => {
  const serializedActions = useSyncExternalStore(
    postLoginActionsStorage.subscribe,
    postLoginActionsStorage.getSerializedActions,
    () => null,
  );
  const actions = postLoginActionsStorage.parseActions(serializedActions);
  const currentAction = actions[0];
  const isNormalized = serializedActions === null || serializedActions === JSON.stringify(actions);
  const { loggedIn } = useLoginState();

  // Clear actions when user logs out
  useEffect(() => {
    if (!loggedIn && postLoginActionsStorage.getActions().length > 0) {
      postLoginActionsStorage.clearActions();
    }
  }, [loggedIn]);

  // StrictMode runs effects twice, so write back only the queue this render saw.
  // Earlier releases stored billing address and email in this queue; this cleans up the storage in the browser.
  useEffect(() => {
    if (!isNormalized && postLoginActionsStorage.getSerializedActions() === serializedActions) {
      postLoginActionsStorage.setActions(postLoginActionsStorage.parseActions(serializedActions));
    }
  }, [isNormalized, serializedActions]);

  // Handler for completing/dismissing an action
  const handleComplete = () => {
    if (postLoginActionsStorage.getSerializedActions() === serializedActions) {
      postLoginActionsStorage.removeFirstAction();
    }
  };

  if (!currentAction) return null;

  // Render appropriate modal based on action type
  switch (currentAction) {
    case PostLoginActionType.CHECK_EMAIL:
      return <ChangeEmailModalDynamic onClose={handleComplete} />;
    case PostLoginActionType.CHECK_BILLING_ADDRESS:
      return <ChangeBillingAddressModalDynamic onClose={handleComplete} />;
    case PostLoginActionType.TAC_RECONFIRMATION:
      return <TACReconfirmationModalDynamic onClose={handleComplete} />;
    default:
      return null;
  }
};
